import { get } from 'lodash-es'
import {
  getFundingShortName,
  FUNDING_TYPE_SHORT_NAMES,
  NEW_INSTRUMENT_TYPES,
  FPTI_FI_CONTINGENCY_TYPE_MAP,
  FUNDING_SOURCE,
  EVALUATE_FUNDING_OPTIONS,
} from 'universal'
import { actionTypes } from '../constants'

export function getFundingQueryDimension(obj = {}) {
  return obj.productName || obj.cardId // FIXME replace with obj.dimension. See #487
}

// TODO refactor into proper selector with reselect's createCachedSelector
// eslint-disable-next-line max-statements
export const selectFundingProps = ({ funding: fundingSlice, returnUri }, ownProps) => {
  const funding = get(fundingSlice, `funding[${ownProps.cardId}]`) // TODO use getFundingQueryDimension
  const fundingStatus = get(funding, 'fundingStatus')
  const haveFundingDetailsBeenFetched = !!get(funding, `fundingChangeEligibility`)
  const fundingType =
    getFundingShortName(get(funding, 'fundingOptionType')) || FUNDING_TYPE_SHORT_NAMES.BUFS // default to bufs as it's less complex than topup
  const primaryFunding = get(funding, 'primaryFundingOption')
  const secondaryFunding = get(funding, 'secondaryFundingOptions') || []
  const acceptDebitAndCredit =
    secondaryFunding.includes(NEW_INSTRUMENT_TYPES.DEBIT_CARD.system) &&
    secondaryFunding.includes(NEW_INSTRUMENT_TYPES.CARD.system)
  const fundingSources = get(funding, 'availableFundingOptions.fundingSources') || []
  const fundingSourcesWithoutPrimaryFunding = fundingSources.filter(
    (instrument) => instrument.type !== primaryFunding,
  )
  const fundingSourcesIds = fundingSourcesWithoutPrimaryFunding.map(
    (fundingSource) => fundingSource.id,
  )
  const addMoneyflowQuery = get(funding, 'addMoneyflowQuery')
  // As of Jan 2020, It is safe to assume that there is only 1 top up amount
  const userPreferenceAmount = get(funding, 'availableFundingOptions.amounts[0]')
  const userPreference = get(funding, 'userPreferences.fundingSource', {})

  const {
    fetchError,
    fetchRetry,
    fetchSuccessAction,
    isFetching,
    isFetchUIBlocking,
    noContent,
  } = fundingSlice
  const isFundingChangeEligible =
    get(funding, 'fundingChangeEligibility') === EVALUATE_FUNDING_OPTIONS.STATUS.ELIGIBLE
  const isFundingChangeContingency =
    get(funding, 'fundingChangeEligibility') === EVALUATE_FUNDING_OPTIONS.STATUS.CONTINGENCY

  const {
    NEGATIVE_BALANCE_DECLINE,
    RISK_DECLINE,
    RISK_STEPUP,
    INVALID_FUNDING_PREFERENCE,
    FUNDING_SOURCES_UNAVAILABLE,
  } = EVALUATE_FUNDING_OPTIONS.CONTINGENCY_CODE
  const fundingOptionsContingencyCode = get(funding, 'statusDetails.code')
  const hasNegativeBalanceContingency =
    isFundingChangeContingency && fundingOptionsContingencyCode === NEGATIVE_BALANCE_DECLINE
  const hasRiskDeclineContingency =
    isFundingChangeContingency && fundingOptionsContingencyCode === RISK_DECLINE
  const stepUpContext = get(funding, 'statusDetails.values', [])
  const hasRiskStepUpContingency =
    isFundingChangeContingency && fundingOptionsContingencyCode === RISK_STEPUP
  const hasInvalidFundingPreference =
    isFundingChangeContingency && fundingOptionsContingencyCode === INVALID_FUNDING_PREFERENCE
  const fundingSourcesAreUnavailable =
    isFundingChangeContingency && fundingOptionsContingencyCode === FUNDING_SOURCES_UNAVAILABLE
  const isFundingActive = get(funding, 'fundingStatus') === 'ACTIVE'
  const allowOptOutPrimaryFunding = get(funding, 'allowOptOutOfPrimaryFundingOption')
  const allowOptOutSecondaryFunding = get(funding, 'allowOptOutOfSecondaryFundingOption')
  const isPrimaryFundingAnAvailableFundingSource = !!fundingSources.find(
    (instrument) => instrument.type === primaryFunding,
  )
  const linkFIflowQuery = get(funding, 'linkFIflowQuery')
  const fundingSourceChoiceAffectsOtherInstruments =
    get(funding, 'affectedInstrumentsDisplayNames', []).length > 1
  const defaultPrimaryFundingOptInStatus = allowOptOutPrimaryFunding
    ? isPrimaryFundingAnAvailableFundingSource
    : true
  const isUsingPrimaryFunding = allowOptOutPrimaryFunding
    ? primaryFunding && primaryFunding === get(funding, 'userPreferences.primaryFundingOption')
    : true

  const isRefreshingList = isFetching && !isFetchUIBlocking && !noContent // FIXME this is brittle
  const affectedInstruments = get(funding, 'affectedInstrumentsDisplayNames')

  function shouldShowPrimaryFunding() {
    // the setting at product funding level says primary funding opt out (aka balance toggle) should not be shown
    if (!allowOptOutPrimaryFunding) {
      return false
    }

    // if primary funding is not available as a funding source, there's nothing to opt out of
    if (!isPrimaryFundingAnAvailableFundingSource) {
      return false
    }

    // when there are no funding sources other than primary funding, you can't opt out of primary funding
    if (!fundingSourcesWithoutPrimaryFunding.length) {
      return false
    }

    // NOTE watch out, forChangingNotViewing is only provided to the reusable Funding component, not all its descendants
    // when user is in funding viewer and user opt out of primary funding
    if (!ownProps.forChangingNotViewing && !isUsingPrimaryFunding) {
      return false
    }

    // NOTE watch out, forChangingNotViewing is only provided to the reusable Funding component, not all its descendants
    if (ownProps.forChangingNotViewing && isRefreshingList) {
      return false
    }

    return true
  }

  const getContingenciesArrayForFpti = (fundingSource) => {
    if (fundingSource?.status !== FUNDING_SOURCE.STATUS.CONTINGENCY) {
      return []
    }

    const contingencyType = fundingSource?.contingencyDetails?.type
    return [FPTI_FI_CONTINGENCY_TYPE_MAP[contingencyType] || contingencyType]
  }

  const fptiFundingSourcesIdsWithContingencies = fundingSourcesWithoutPrimaryFunding.map(
    (fundingSource) => {
      return {
        id: fundingSource.id,
        contingency: getContingenciesArrayForFpti(fundingSource),
      }
    },
  )

  return {
    fetchingState: fundingSlice,
    returnUri,
    fundingType,
    fundingStatus,
    isFundingActive,
    isFundingChangeEligible,
    fundingSources: fundingSourcesWithoutPrimaryFunding, // TODO move into Funding.js or Instruments.js
    fundingSourcesIds,
    primaryFunding,
    allowOptOutPrimaryFunding,
    allowOptOutSecondaryFunding,
    isPrimaryFundingAnAvailableFundingSource,
    defaultPrimaryFundingOptInStatus,
    isUsingPrimaryFunding,
    linkFIflowQuery,
    secondaryFunding,
    acceptDebitAndCredit,
    fundingSourceChoiceAffectsOtherInstruments,
    userPreference,
    userPreferenceAmount,
    addMoneyflowQuery,
    isFetching,
    fetchError,
    fetchRetry,
    setSuccess: fetchSuccessAction === actionTypes.SET_FUNDING_SUCCESS,
    removeSuccess: fetchSuccessAction === actionTypes.REMOVE_FUNDING_SUCCESS,
    hasNegativeBalanceContingency,
    hasRiskDeclineContingency,
    hasRiskStepUpContingency,
    hasInvalidFundingPreference,
    fundingSourcesAreUnavailable,
    stepUpContext,
    isRefreshingList,
    affectedInstruments,
    haveFundingDetailsBeenFetched,
    showPrimaryFunding: shouldShowPrimaryFunding(),
    fptiFundingSourcesIdsWithContingencies,
  }
}
